"use client";

import Link from "next/link";
import { getImageProps } from "next/image";
import { useEffect, useState, useSyncExternalStore } from "react";
import type { FocusEvent, KeyboardEvent, ReactNode } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { ImageWithFallback } from "@/components/ui/ImageWithFallback";
import { formatPrice } from "@/lib/utils";
import { isOptimizable } from "@/lib/image";
import {
  MAX_HERO_BANNER_SLIDES,
  MAX_HERO_PRODUCT_SLIDES,
} from "@/lib/hero-slideshow";
import type { Banner, ProductWithDiscount } from "@/types";

// HeroSection.tsx가 쓰는 값과 맞춰야 슬라이드가 바뀔 때 배너 높이가
// 흔들리지 않는다. HeroSection.tsx는 그대로 두어야 하므로(디자인 변경 금지)
// 상수를 공유하는 대신 같은 값을 여기서도 유지한다.
const HERO_MIN_HEIGHT = "85vh";

// 원래는 NoticeBar와 같은 리듬(5초 자동 전환 + 0.5초 페이드)을 그대로
// 썼지만, 사장님 피드백("메인 슬라이드는 좀 확 바뀌는 느낌")은 이 히어로
// 슬라이드쇼에만 해당하는 문제였다. 전면 사진이 500ms 만에 사라지면 기계적으로
// "뚝" 끊기므로 페이드를 900ms로 늘렸고, 그만큼 화면이 계속 전환 중인
// 느낌이 들지 않도록 대기 시간도 6초로 함께 늘렸다. 작은 텍스트 티커인
// NoticeBar는 별개 UI라 5초/0.5초를 그대로 유지한다.
const SLIDE_INTERVAL_MILLISECONDS = 6000;
const SLIDE_TRANSITION_MILLISECONDS = 900;

// Tailwind의 transition-opacity 기본 이징이 마침 이 값과 같지만, 큰 사진이
// 감속하며 "안착하는" 느낌을 주는 의도를 코드에서 바로 읽을 수 있도록
// 명시적으로 적어둔다.
const SLIDE_TRANSITION_EASING = "cubic-bezier(0.4, 0, 0.2, 1)";

// 점(dot) 인디케이터는 작은 UI라 이미지 크로스페이드와 같은 속도로 움직이면
// 클릭에 대한 반응이 굼떠 보인다. 이미지 전환 속도와 분리해 계속 짧게 유지한다.
const SLIDE_INDICATOR_TRANSITION_MILLISECONDS = 300;

// 상품 슬라이드에서 텍스트가 사진보다 살짝 늦게 나타나도록 주는 지연.
// 시선이 먼저 사진에 닿고 그다음 텍스트로 옮겨가게 한다. 빠질 때는 지연 없이
// 사진과 함께 바로 사라진다(ProductSlide 참고).
const PRODUCT_TEXT_FADE_DELAY_MILLISECONDS = 150;
const PRODUCT_TEXT_FADE_MILLISECONDS = 600;

const PLACEHOLDER_IMAGE = "/placeholder-product.svg";

const PRODUCT_SLIDE_SCRIM =
  "linear-gradient(180deg, rgba(18,42,30,0.55) 0%, rgba(18,42,30,0.72) 100%)";

// <picture>의 <source media=""> 판정 기준. Tailwind가 쓰는 md 브레이크포인트
// (768px, globals.css 참고) 이상에서 데스크톱용 이미지를 쓰고, 그보다
// 좁은 화면은 <source>가 매치되지 않아 모바일용 <img>로 자연스럽게
// 떨어진다(공식 아트 디렉션 예제와 같은 패턴, 아래 ArtDirectedBannerImage
// 참고).
const DESKTOP_BANNER_MEDIA_QUERY = "(min-width: 768px)";

// 히어로 배너 아트 디렉션(데스크톱/모바일에 다른 이미지)에 쓰는 크기.
// 관리자 배너 페이지에서 사장님께 안내하는 권장 크기와 반드시 같아야
// 한다(src/app/admin/banners/page.tsx 참고).
//
// 데스크톱은 1920이 아니라 1600이다: 업로드 파이프라인
// (src/app/api/upload/route.ts의 MAX_DIMENSION)이 원본을 가로/세로 최대
// 1600px로 줄이고, next.config.ts의 deviceSizes도 최대 1536이라 1920은
// 두 군데서 버려지는 픽셀을 요청하는 셈이다. 1600을 넘겨 다시 올리면
// 안 된다 - 파이프라인이 그대로 잘라낸다.
const HERO_BANNER_DESKTOP_WIDTH_PIXELS = 1600;
const HERO_BANNER_DESKTOP_HEIGHT_PIXELS = 800;
const HERO_BANNER_MOBILE_WIDTH_PIXELS = 800;
const HERO_BANNER_MOBILE_HEIGHT_PIXELS = 1400;

// prefers-reduced-motion 판정에 cart.ts/consent.ts와 같은 이유로
// useSyncExternalStore를 쓴다: matchMedia는 이 컴포넌트만 쓰는 외부 소스라
// 별도 lib 파일로 뺄 필요는 없지만 패턴은 CookieBanner.tsx/useCart.ts와
// 동일하다. useEffect 안에서 곧바로 setState를 호출하던 이전 방식은
// react-hooks/set-state-in-effect 룰을 위반했고, 마운트마다 렌더가 한 번 더
// 발생했다. 렌더링 결과(처음 보이는 슬라이드)에는 영향이 없으므로 서버
// 스냅샷은 항상 false(애니메이션 켜짐)로 고정해 하이드레이션 시점의 마크업과
// 맞추고, 마운트 직후 실제 값으로 react가 알아서 재렌더한다.
const REDUCED_MOTION_MEDIA_QUERY = "(prefers-reduced-motion: reduce)";

// getServerSnapshot은 매 호출마다 같은 값을 돌려줘야 한다(cart.ts의
// getServerCartSnapshot 주석 참고). boolean은 값 자체가 항상 안정적이지만,
// 의도를 명시적으로 남기기 위해 모듈 스코프 상수로 고정한다.
const SERVER_REDUCED_MOTION_SNAPSHOT = false;

let cachedReducedMotionQuery: MediaQueryList | null = null;

function getReducedMotionQuery(): MediaQueryList {
  if (cachedReducedMotionQuery === null) {
    cachedReducedMotionQuery = window.matchMedia(REDUCED_MOTION_MEDIA_QUERY);
  }
  return cachedReducedMotionQuery;
}

function subscribeToReducedMotion(listener: () => void): () => void {
  const query = getReducedMotionQuery();
  query.addEventListener("change", listener);
  return () => query.removeEventListener("change", listener);
}

function getReducedMotionSnapshot(): boolean {
  return getReducedMotionQuery().matches;
}

function getServerReducedMotionSnapshot(): boolean {
  return SERVER_REDUCED_MOTION_SNAPSHOT;
}

// 탭이 백그라운드에 있는 동안에도 setInterval은 그대로 돌아 current가 여러
// 번 바뀌지만, 브라우저가 보이지 않는 탭은 화면을 그리지(paint) 않으므로
// 그 변화가 눈에 보이지 않는다. 탭에 돌아오면 그동안 쌓인 변화가 한 번의
// 그리기에 반영되며 슬라이드가 여러 칸 급하게 넘어간 것처럼 보인다. 탭이
// 숨겨지면 자동전환 자체를 멈추고(interval을 새로 만들지 않음), 다시
// 보이면 그 시점부터 새 SLIDE_INTERVAL_MILLISECONDS를 시작해 이 문제를
// 없앤다.
function subscribeToVisibility(listener: () => void): () => void {
  document.addEventListener("visibilitychange", listener);
  return () => document.removeEventListener("visibilitychange", listener);
}

function getVisibilitySnapshot(): boolean {
  return document.hidden;
}

function getServerVisibilitySnapshot(): boolean {
  // 서버에는 document가 없다. reduced motion과 같은 이유로, 처음
  // 그려지는 시점은 항상 "보이는 탭"이라고 가정하고 마운트 직후 실제
  // 값으로 react가 재렌더한다.
  return false;
}

interface HeroSlideshowProps {
  heroSlide: ReactNode;
  products: ProductWithDiscount[];
  banners: Banner[];
}

export function HeroSlideshow({
  heroSlide,
  products,
  banners,
}: HeroSlideshowProps) {
  // page.tsx의 히어로 배너 쿼리가 이미 MAX_HERO_BANNER_SLIDES개까지만
  // 가져오므로 보통은 자를 게 없지만, 안전망으로 여기서도 한 번 더 자른다.
  const bannerSlides = banners.slice(0, MAX_HERO_BANNER_SLIDES);
  // 사장님이 만든 배너가 하나라도 있으면 그것만 보여주고 상품 슬라이드는
  // 완전히 무시한다. 디자인된 배너와 자동 생성된 상품 카드가 나란히
  // 섞이면 톤이 어긋나 보이기 때문에 둘을 섞지 않는다.
  const productSlides =
    bannerSlides.length > 0 ? [] : products.slice(0, MAX_HERO_PRODUCT_SLIDES);
  const totalSlides = 1 + bannerSlides.length + productSlides.length;

  const [current, setCurrent] = useState(0);
  // 마우스 오버로 멈추는 동작은 사장님 요청으로 뺐다(마우스가 올라가 있어도
  // 계속 넘어가야 한다). 포커스 정지는 남겨둔다. 화살표/점/CTA를 탭으로
  // 훑는 도중에 슬라이드가 바뀌면 포커스가 엉뚱한 곳으로 튀므로, 이건
  // 취향이 아니라 접근성 문제다.
  const [isFocusPaused, setIsFocusPaused] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const prefersReducedMotion = useSyncExternalStore(
    subscribeToReducedMotion,
    getReducedMotionSnapshot,
    getServerReducedMotionSnapshot,
  );
  const isDocumentHidden = useSyncExternalStore(
    subscribeToVisibility,
    getVisibilitySnapshot,
    getServerVisibilitySnapshot,
  );

  const autoAdvancing =
    totalSlides > 1 &&
    isPlaying &&
    !isFocusPaused &&
    !prefersReducedMotion &&
    !isDocumentHidden;

  useEffect(() => {
    if (!autoAdvancing) {
      return;
    }
    const interval = setInterval(() => {
      setCurrent((value) => (value + 1) % totalSlides);
    }, SLIDE_INTERVAL_MILLISECONDS);
    return () => clearInterval(interval);
  }, [autoAdvancing, totalSlides]);

  // 배너도 상품도 없으면 원래 히어로만 그대로 내보낸다. 슬라이드 래퍼나
  // 컨트롤도 붙이지 않아 지금과 완전히 같은 화면을 유지한다.
  if (bannerSlides.length === 0 && productSlides.length === 0) {
    return <>{heroSlide}</>;
  }

  function goTo(nextIndex: number) {
    setCurrent(((nextIndex % totalSlides) + totalSlides) % totalSlides);
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsFocusPaused(false);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case "ArrowLeft": {
        goTo(current - 1);
        break;
      }
      case "ArrowRight": {
        goTo(current + 1);
        break;
      }
      default: {
        break;
      }
    }
  }

  // 배너가 나오는 중이면 라벨도 그에 맞게 바꿔, 스크린리더 사용자가 실제
  // 화면과 다른 안내를 듣지 않게 한다.
  const heroRegionLabel =
    bannerSlides.length > 0
      ? "현들팜 소개 및 배너"
      : "현들팜 소개 및 추천 상품";

  return (
    <div
      role="region"
      aria-roledescription="배너 슬라이드쇼"
      aria-label={heroRegionLabel}
      aria-live={autoAdvancing ? "off" : "polite"}
      className="relative overflow-hidden"
      style={{
        minHeight: HERO_MIN_HEIGHT,
        // 슬라이드는 opacity로만 크로스페이드한다. 전환 중간 지점에는 나가는
        // 슬라이드와 들어오는 슬라이드가 둘 다 부분 투명이라 이 컨테이너의
        // 배경이 비쳐 보인다. body 배경(--color-cream)이 그대로 비치면
        // 어두운 톤의 슬라이드 사이에서 흰색이 번쩍이므로, 히어로 그라디언트의
        // 가장 어두운 톤이자 상품 슬라이드 스크림과 같은 색으로 채워 비쳐도
        // 눈에 띄지 않게 한다.
        backgroundColor: "var(--color-primary-dark)",
      }}
      onFocus={() => setIsFocusPaused(true)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <HeroSlideFrame
        active={current === 0}
        index={0}
        totalSlides={totalSlides}
        label="현들팜 소개"
      >
        {heroSlide}
      </HeroSlideFrame>

      {bannerSlides.map((banner, bannerIndex) => (
        <HeroSlideFrame
          key={banner.id}
          active={current === bannerIndex + 1}
          index={bannerIndex + 1}
          totalSlides={totalSlides}
          label={banner.title}
        >
          <BannerSlide banner={banner} />
        </HeroSlideFrame>
      ))}

      {productSlides.map((product, productIndex) => (
        <HeroSlideFrame
          key={product.id}
          active={current === productIndex + 1}
          index={productIndex + 1}
          totalSlides={totalSlides}
          label={product.name}
        >
          <ProductSlide
            product={product}
            active={current === productIndex + 1}
            priority={productIndex === 0}
          />
        </HeroSlideFrame>
      ))}

      <button
        type="button"
        className="absolute left-2 top-1/2 z-10 flex min-h-12 min-w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/25 text-white transition-colors hover:bg-black/45 md:left-5"
        aria-label="이전 슬라이드"
        onClick={() => goTo(current - 1)}
      >
        <ChevronLeft className="h-6 w-6" aria-hidden="true" />
      </button>
      <button
        type="button"
        className="absolute right-2 top-1/2 z-10 flex min-h-12 min-w-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/25 text-white transition-colors hover:bg-black/45 md:right-5"
        aria-label="다음 슬라이드"
        onClick={() => goTo(current + 1)}
      >
        <ChevronRight className="h-6 w-6" aria-hidden="true" />
      </button>

      <div className="absolute inset-x-0 bottom-4 z-10 flex flex-col items-center gap-2 md:bottom-6">
        <div
          role="group"
          aria-label="슬라이드 바로가기"
          className="flex items-center gap-1"
        >
          {Array.from({ length: totalSlides }, (_, dotIndex) => (
            <button
              key={dotIndex}
              type="button"
              className="flex min-h-12 min-w-12 items-center justify-center"
              aria-label={`${dotIndex + 1}번째 슬라이드: ${getSlideLabel(dotIndex, bannerSlides, productSlides)}`}
              aria-current={current === dotIndex}
              onClick={() => goTo(dotIndex)}
            >
              <span
                className="block rounded-full transition-all"
                style={{
                  width: current === dotIndex ? 22 : 8,
                  height: 8,
                  backgroundColor:
                    current === dotIndex
                      ? "var(--color-gold)"
                      : "rgba(255,255,255,0.55)",
                  transitionDuration: `${SLIDE_INDICATOR_TRANSITION_MILLISECONDS}ms`,
                }}
              />
            </button>
          ))}
        </div>

        {!prefersReducedMotion && (
          <button
            type="button"
            className="flex min-h-12 min-w-12 items-center justify-center rounded-full bg-black/25 text-white transition-colors hover:bg-black/45"
            aria-label={
              isPlaying
                ? "슬라이드 자동 전환 멈추기"
                : "슬라이드 자동 전환 시작"
            }
            onClick={() => setIsPlaying((value) => !value)}
          >
            {isPlaying ? (
              <Pause className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Play className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function HeroSlideFrame({
  active,
  index,
  totalSlides,
  label,
  children,
}: {
  active: boolean;
  index: number;
  totalSlides: number;
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      role="group"
      aria-roledescription="슬라이드"
      aria-label={`${index + 1} / ${totalSlides}: ${label}`}
      aria-hidden={!active}
      inert={!active}
      className="absolute inset-0 transition-opacity"
      style={{
        opacity: active ? 1 : 0,
        transitionDuration: `${SLIDE_TRANSITION_MILLISECONDS}ms`,
        transitionTimingFunction: SLIDE_TRANSITION_EASING,
        pointerEvents: active ? "auto" : "none",
      }}
    >
      {children}
    </div>
  );
}

function BannerSlide({ banner }: { banner: Banner }) {
  // 히어로 배너 쿼리는 image가 있는 행만 가져오므로 실제로는 항상 값이
  // 있지만, Banner 타입은 NOTICE 배너와 공유해 image가 optional이라
  // 타입상으로는 없을 수 있다. 방어적으로 플레이스홀더를 둔다.
  const image = banner.image ?? PLACEHOLDER_IMAGE;

  // 사진 자체가 사장님이 직접 디자인한 결과물이라 텍스트를 얹지 않는다.
  // title은 alt/aria-label로만 쓰이는 접근성용 설명이다.
  //
  // 슬라이드 0(초록 인트로)이 실제 첫 화면이라 배너는 어느 것도 LCP
  // 요소가 아니다. priority를 주지 않는다.
  const frame = (
    <div className="relative h-full w-full overflow-hidden">
      {banner.mobileImage ? (
        <ArtDirectedBannerImage
          title={banner.title}
          image={image}
          mobileImage={banner.mobileImage}
        />
      ) : (
        <ImageWithFallback
          src={image}
          alt={banner.title}
          fill
          sizes="100vw"
          unoptimized={!isOptimizable(image)}
          className="object-cover"
          fallbackClassName="absolute inset-0"
        />
      )}
    </div>
  );

  // 링크가 없으면 href 없는 앵커를 만들지 않고 이미지만 보여준다.
  if (!banner.link) {
    return frame;
  }

  return (
    <Link href={banner.link} className="block h-full w-full">
      {frame}
    </Link>
  );
}

// mobileImage가 있을 때만 쓰는 아트 디렉션(데스크톱/모바일에 다른 이미지)
// 경로. <Image fill>을 <picture><source>와 같이 쓰지 않는 이유: <Image>는
// 자기 자신의(데스크톱) srcSet만 들고 있어서 <source>가 매치돼도 브라우저가
// 실제로 쓰는 <img>의 srcSet은 그대로라 최적화가 전혀 안 먹는다(모바일에서
// 업로드 원본을 그대로 받게 됨). getImageProps는 렌더링 없이 최적화
// URL(srcSet)만 계산해 주는 순수 함수라 이 문제와 무관하다 - 데스크톱/
// 모바일 각각의 srcSet을 뽑아 진짜 <picture>/<img>에 직접 꽂는다(Next.js
// 아트 디렉션 공식 패턴). mobileImage가 없는 흔한 경우는 이 컴포넌트를
// 아예 쓰지 않고 위 BannerSlide에서 바로 ImageWithFallback을 쓴다 -
// <picture>가 필요 없는 경로까지 원시 <img>로 내릴 이유가 없다.
function ArtDirectedBannerImage({
  title,
  image,
  mobileImage,
}: {
  title: string;
  image: string;
  mobileImage: string;
}) {
  // <picture> 안에는 <source>/<img>만 올 수 있어 ImageWithFallback을
  // 그대로 넣을 수 없으므로, 같은 폴백 마크업을 직접 들고 있다가 실패 시
  // <picture> 전체를 대체한다.
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div
        className="flex h-full w-full items-center justify-center bg-[var(--color-primary)] text-white"
        role="img"
        aria-label={title}
      >
        <span className="text-4xl" aria-hidden="true">
          🎃
        </span>
      </div>
    );
  }

  const common = { alt: title, sizes: "100vw" };
  // src도 같이 꺼내는 이유: unoptimized(최적화 대상 호스트가 아닌 이미지,
  // isOptimizable 참고)일 때 getImageProps는 srcSet을 undefined로 돌려준다.
  // 그대로 <source srcSet={undefined}>가 되면 데스크톱에서 이 source가
  // 미디어쿼리에 매치되고도 후보 이미지가 없어 배너가 통째로 안 뜬다.
  // 그 경우 원본 src 하나를 srcSet으로 넣어 최소한 이미지는 나오게 한다.
  const {
    props: { srcSet: desktopSrcSet, src: desktopSrc },
  } = getImageProps({
    ...common,
    width: HERO_BANNER_DESKTOP_WIDTH_PIXELS,
    height: HERO_BANNER_DESKTOP_HEIGHT_PIXELS,
    unoptimized: !isOptimizable(image),
    src: image,
  });
  // srcSet을 따로 떼지 않고 props 전체를 그대로 쓴다 - <source>가
  // 매치되지 않는 좁은 화면에서도 <img> 자체의 srcSet으로 배율(2x 등)에
  // 맞는 크기가 그대로 반응형으로 동작해야 하기 때문이다.
  const { props: mobileImgProps } = getImageProps({
    ...common,
    width: HERO_BANNER_MOBILE_WIDTH_PIXELS,
    height: HERO_BANNER_MOBILE_HEIGHT_PIXELS,
    unoptimized: !isOptimizable(mobileImage),
    src: mobileImage,
  });

  return (
    <picture>
      <source
        media={DESKTOP_BANNER_MEDIA_QUERY}
        srcSet={desktopSrcSet ?? desktopSrc}
      />
      {/* alt는 mobileImgProps 스프레드에 이미 들어있지만, eslint의
          jsx-a11y/alt-text는 스프레드 안까지 정적으로 보지 못해 오탐한다.
          값은 같지만 명시적으로 한 번 더 적어 정적 검사를 통과시킨다. */}
      <img
        {...mobileImgProps}
        alt={title}
        onError={() => setHasError(true)}
        className="absolute inset-0 h-full w-full object-cover"
      />
    </picture>
  );
}

function ProductSlide({
  product,
  active,
  priority,
}: {
  product: ProductWithDiscount;
  active: boolean;
  priority: boolean;
}) {
  const image =
    product.images.length > 0 ? product.images[0] : PLACEHOLDER_IMAGE;
  const isSoldOut = !product.isAvailable || product.effectiveStock === 0;

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* 이미지는 이름/설명이 이미 텍스트로 나오므로 장식용으로 처리한다
          (alt=""). ImageWithFallback이 이미지가 없거나 깨졌을 때 브랜드
          색상 배경으로 대체해준다. */}
      <ImageWithFallback
        src={image}
        alt=""
        fill
        sizes="100vw"
        unoptimized={!isOptimizable(image)}
        priority={priority}
        className="object-cover"
        fallbackClassName="absolute inset-0"
      />
      <div
        className="absolute inset-0"
        style={{ background: PRODUCT_SLIDE_SCRIM }}
      />

      <div className="relative flex h-full items-center justify-center px-4 text-center">
        {/* 텍스트는 사진보다 PRODUCT_TEXT_FADE_DELAY_MILLISECONDS만큼
            늦게 나타나 시선이 사진에 먼저 닿게 한다. 빠질 때는 지연 없이
            사진과 함께 바로 사라진다. */}
        <div
          className="container py-16 md:py-24 transition-opacity"
          style={{
            opacity: active ? 1 : 0,
            transitionDuration: `${PRODUCT_TEXT_FADE_MILLISECONDS}ms`,
            transitionDelay: active
              ? `${PRODUCT_TEXT_FADE_DELAY_MILLISECONDS}ms`
              : "0ms",
            transitionTimingFunction: SLIDE_TRANSITION_EASING,
          }}
        >
          <div className="mx-auto max-w-lg">
            <div className="mb-5 flex flex-wrap justify-center gap-2">
              {isSoldOut ? (
                <span className="badge bg-[var(--color-text-muted)] text-xs text-white">
                  품절
                </span>
              ) : (
                <>
                  {product.isOnDiscount && (
                    <span className="badge badge-gold text-xs">
                      {product.discountRate}% 할인
                    </span>
                  )}
                  {product.isPreorder && (
                    <span className="badge border border-white/60 bg-white/10 text-xs text-white">
                      예약주문
                    </span>
                  )}
                </>
              )}
            </div>

            <h2
              className="mb-3 text-3xl font-bold leading-[1.2] text-white md:text-4xl lg:text-[3rem]"
              style={{ fontFamily: "var(--font-serif)" }}
            >
              {product.name}
            </h2>

            {product.description && (
              <p
                className="mx-auto mb-6 line-clamp-2 text-base text-white/75 md:text-lg"
                style={{ lineHeight: 1.7 }}
              >
                {product.description}
              </p>
            )}

            <div className="mb-8 flex items-baseline justify-center gap-2">
              <SlidePrice product={product} />
              <span className="text-sm text-white/70">
                {getUnitLabel(product)}
              </span>
            </div>

            <div className="flex justify-center">
              {isSoldOut ? (
                <div
                  className="btn-outline cursor-not-allowed border-white/50 text-white opacity-70"
                  style={{ backgroundColor: "transparent" }}
                  aria-label={`${product.name}은 품절되었습니다`}
                >
                  품절되었습니다
                </div>
              ) : (
                <Link
                  href={`/products/${product.id}`}
                  className="btn-gold"
                  style={{ boxShadow: "0 4px 20px rgba(201,168,76,0.4)" }}
                >
                  {product.isPreorder ? "예약하기" : "주문하기"}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SlidePrice({ product }: { product: ProductWithDiscount }) {
  const priceClassName = "text-2xl font-bold md:text-3xl";
  const priceStyle = { color: "var(--color-gold-light)" };

  if (product.hasMultipleOptions) {
    return (
      <p className={priceClassName} style={priceStyle}>
        {formatPrice(product.finalPrice)}
        <span className="text-base font-medium">~</span>
      </p>
    );
  }

  if (product.hasOptions) {
    return (
      <p className={priceClassName} style={priceStyle}>
        {formatPrice(product.finalPrice)}
      </p>
    );
  }

  if (product.isOnDiscount) {
    return (
      <p className={priceClassName} style={priceStyle}>
        <span className="mr-2 text-sm font-normal text-white/50 line-through">
          {formatPrice(product.price)}
        </span>
        {formatPrice(product.finalPrice)}
      </p>
    );
  }

  return (
    <p className={priceClassName} style={priceStyle}>
      {formatPrice(product.finalPrice)}
    </p>
  );
}

function getUnitLabel(product: ProductWithDiscount): string {
  if (product.hasMultipleOptions) {
    return `${product.startingUnit} · 단위 선택 가능`;
  }
  if (product.hasOptions) {
    return product.startingUnit;
  }
  return product.unit;
}

function getSlideLabel(
  index: number,
  bannerSlides: Banner[],
  productSlides: ProductWithDiscount[],
): string {
  if (index === 0) {
    return "현들팜 소개";
  }
  if (bannerSlides.length > 0) {
    const banner = bannerSlides[index - 1];
    return banner ? banner.title : "배너 슬라이드";
  }
  const product = productSlides[index - 1];
  return product ? product.name : "상품 슬라이드";
}
